import { useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Sidebar, type NavItemConfig } from './Sidebar';

// Cortex AppShell (DESIGN_LANGUAGE_SPEC §24). Composes the collapsible Sidebar, a sticky
// blurred top bar, and a scrollable content region (min-h-full p-8). Real routes arrive in
// later chunks; this shell renders either explicit children or a react-router <Outlet />.

export interface AppShellProps {
  navItems: NavItemConfig[];
  /** Title shown in the sticky header. */
  pageTitle?: string | undefined;
  /** Branding for the sidebar header. */
  brand?: ReactNode | undefined;
  /** Optional sidebar footer (e.g. user menu). */
  sidebarFooter?: ReactNode | undefined;
  /** Controls rendered on the right of the header (e.g. ThemeToggle). */
  headerActions?: ReactNode | undefined;
  /** Start with the sidebar collapsed. */
  defaultCollapsed?: boolean | undefined;
  /** Page content. Falls back to <Outlet /> when omitted (route-driven layout). */
  children?: ReactNode | undefined;
}

export function AppShell({
  navItems,
  pageTitle,
  brand,
  sidebarFooter,
  headerActions,
  defaultCollapsed = false,
  children,
}: AppShellProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div
      className="theme-bg-primary flex h-screen overflow-hidden transition-colors duration-300"
    >
      <Sidebar
        items={navItems}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        brand={brand}
        footer={sidebarFooter}
      />

      <main className="flex flex-1 flex-col overflow-hidden">
        {/* Sticky, blurred top bar. Surface colors come from the theme token layer
            (--theme-bg-header / --theme-border-header), so the per-theme switch lives
            in tokens.css rather than an inline ternary — FIX-17(a). */}
        <header
          className="sticky top-0 z-10 flex h-16 items-center justify-between border-b px-6 backdrop-blur-xl"
          style={{
            background: 'var(--theme-bg-header)',
            borderColor: 'var(--theme-border-header)',
          }}
        >
          <h1
            className={`text-lg font-semibold ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            {pageTitle}
          </h1>
          {headerActions && (
            <div className="flex items-center gap-2">{headerActions}</div>
          )}
        </header>

        {/* Scrollable content */}
        <div
          className="flex-1 overflow-y-scroll"
          style={{ scrollbarGutter: 'stable' }}
        >
          <div className="min-h-full p-8">{children ?? <Outlet />}</div>
        </div>
      </main>
    </div>
  );
}
